import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
// i18next
import { useTranslation } from 'react-i18next';
// Types
import QuizTypes from 'types/QuizTypes';
// Components
import { Input, Select } from 'components/Controls';
import MultiLanguageTextField from 'components/MultiLanguageTextField';
import { Button } from 'components/Buttons';
import TimeoutField from '../TimeoutField';
// Icons
import { IoLocationSharp } from 'react-icons/io5';
import { FaMinus, FaPlus } from 'react-icons/fa';
// Utilities
import { isRequired, noWhitespace } from 'utilities/validation';

type Props = {
  questionRef: string;
}

const minTolerance = 0.02;
const maxTolerance = 0.25;
const step = 0.01;

const ImagePinForm: FC<Props> = ({ questionRef }) => {
  const { t } = useTranslation();

  const imageRef = useRef<HTMLImageElement | null>(null);
  const [imageSize, setImageSize] = useState({ width: 0, height: 0 });

  const { control, formState: { errors }, watch, setValue } = useFormContext();

  const watchedQuestions = watch('questions');
  const questionIndex = watchedQuestions.findIndex((question: any) => question.ref === questionRef);

  const getQuestionError = useCallback((path: string) => {
    if (!errors?.questions) return '';
    const questionErrors = errors.questions[questionIndex];
    const fieldError = path.split('.').reduce<any>((acc, key) => (acc ? acc[key] : undefined), questionErrors);
    return fieldError?.message ?? '';
  }, [errors, questionIndex]);

  const watchedQuestionTimeout = watch(`questions[${questionIndex}].timeout`);
  const watchedQuizType = watch('type');
  const defaultQuestionTimeout = watchedQuizType === QuizTypes.Prediction ? 30 : 10;

  const { x, y, tolerance, image } = watch(`questions.${questionIndex}.config`);

  const setCoordinates = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const clickY = e.clientY - rect.top;
    const clamp = (v: number) => Math.min(1, Math.max(0, v));
    setValue(`questions.${questionIndex}.config.x`, clamp(clickX / rect.width));
    setValue(`questions.${questionIndex}.config.y`, clamp(clickY / rect.height));
  };

  const updateImageSize = () => {
    if (!imageRef.current) return;
    const { width, height } = imageRef.current.getBoundingClientRect();
    setImageSize({ width, height });
  };

  useEffect(() => {
    if (!imageRef.current) return;
    const observer = new ResizeObserver(() => { updateImageSize(); });
    observer.observe(imageRef.current);
    return () => observer.disconnect();
  }, []);

  const base = Math.min(imageSize.width, imageSize.height);
  const widthCoef = base && imageSize.width ? base / imageSize.width : 0;
  const heightCoef = base && imageSize.height ? base / imageSize.height : 0;

  const changeTolerance = (delta: number) => {
    const nextValue = Math.round(
      Math.min(maxTolerance, Math.max(minTolerance, tolerance + delta)) * 100
    ) / 100;
    setValue(`questions.${questionIndex}.config.tolerance`, nextValue);
  };

  const mapsOptions = [
    { value: 'https://media.quizwizards.com/image_map_default.png', label: 'Europe' },
    { value: 'https://media.quizwizards.com/map_ire.png', label: 'Ireland' },
    { value: 'https://media.quizwizards.com/map_uk.png', label: 'United Kingdom' },
    { value: 'https://media.quizwizards.com/map_us.png', label: 'United States' },
  ];

  if (questionIndex === undefined || questionIndex < 0) return null;

  return (
    <div className="w-full -mx-6 -y-4 px-6 pb-3">
      <Controller
        control={control}
        name={`questions.${questionIndex}.config.image`}
        rules={{ required: isRequired }}
        render={({ field }) => (
          <Select
            {...field}
            id={`questions.${questionIndex}.config.image`}
            options={mapsOptions}
            required
            error={Boolean(getQuestionError('config.image'))}
            errorText={getQuestionError('config.image')}
          />
        )}
      />
      <MultiLanguageTextField
        rules={{ required: isRequired, validate: noWhitespace }}
        name={`questions.${questionIndex}.title`}
        label={t('labels.question')}
        required={true}
        error={Boolean(getQuestionError('title'))}
        errorText={getQuestionError('title')}
        customKey={`questions.${questionRef}.title`}
        className="mb-4"
      />
      <Controller
        control={control} name={`questions.${questionIndex}.score`}
        render={({ field: { onChange, value } }) => (
          <Input
            type="number"
            label={t('labels.score')} id={`questions.${questionIndex}.score`} name={`questions.${questionIndex}.score`}
            value={value}
            onChange={onChange}
            helperText={watchedQuestionTimeout
              ? t('pages.quizForm.questionScoreHintWithTimeout', { score: (watchedQuestionTimeout || defaultQuestionTimeout) * 100 })
              : t('pages.quizForm.questionScoreHint', { score: watchedQuizType === QuizTypes.Prediction ? '3,000' : '1,000' })
            }
          />
        )}
      />
      <TimeoutField questionIndex={questionIndex} />
      <MultiLanguageTextField
        name={`questions.${questionIndex}.correctAnswerDescription`}
        label={t('pages.quizForm.correctAnswerDescriptionLabel')}
        customKey={`questions.${questionRef}.correctAnswerDescription`}
        className="mb-4"
      />
      <div className="relative w-full" onClick={setCoordinates}>
        <img
          ref={imageRef}
          src={image}
          alt=""
          className="w-full block"
          onLoad={updateImageSize}
        />
        <div
          className="absolute z-10"
          style={{ top: `${y * 100}%`, left: `${x * 100}%`, transform: 'translate(-50%, -100%)' }}
        >
          <IoLocationSharp className="text-4xl text-white pointer-events-none" />
        </div>
        {tolerance && imageSize.height > 0 && (
          <div
            className="absolute rounded-full border-2 border-blue-500"
            style={{
              width: `${tolerance * 200 * widthCoef}%`,
              height: `${tolerance * 200 * heightCoef}%`,
              left: `${x * 100}%`,
              top: `${y * 100}%`,
              transform: 'translate(-50%, -60%)',
              pointerEvents: 'none',
            }}
          />
        )}
      </div>
      <div className="mt-4 flex gap-2">
        <Button
          variant="contained"
          className="flex items-center gap-2"
          onClick={() => changeTolerance(-step)}
          disabled={tolerance <= minTolerance}
        >
          <FaMinus />
          Decrease Tolerance
        </Button>
        <Button
          variant="contained"
          className="flex items-center gap-2"
          onClick={() => changeTolerance(+step)}
          disabled={tolerance >= maxTolerance}
        >
          <FaPlus />
          Increase Tolerance
        </Button>
      </div>
    </div>
  );
};

export default ImagePinForm;
